import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, CreditCard, Smartphone, ExternalLink } from "lucide-react";
import api from "../lib/api";

const COUNTRY_INFO = {
  US: { flag: "🇺🇸", title: "Estados Unidos", price: "20 USD" },
  MX: { flag: "🇲🇽", title: "México", price: "380 MXN" },
  EU: { flag: "🇪🇺", title: "España / Europa", price: "20 EUR" },
  WW: { flag: "🌍", title: "Otro país", price: "≈ 20 USD (moneda local)" },
};

export default function Payment() {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState([]);
  const [fallbackUrl, setFallbackUrl] = useState(null); // shown if browser blocks the redirect
  const continueBtnRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/payments/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const selectCountry = (country) => {
    setSelected(country);
    setFallbackUrl(null);
    // Auto-scroll to the Continue button so the two-step flow is discoverable on mobile
    setTimeout(() => {
      continueBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const handlePay = async () => {
    if (!selected) {
      toast.error("Selecciona tu país");
      return;
    }
    setLoading(true);
    setFallbackUrl(null);
    try {
      const { data } = await api.post(
        "/payments/checkout",
        {
          country: selected,
          origin_url: window.location.origin,
        },
        { timeout: 20000 }
      );
      if (!data?.url) {
        console.error("Checkout response missing url:", data);
        toast.error("Respuesta inválida del servidor. Intenta de nuevo.");
        setLoading(false);
        return;
      }
      // Try to redirect. If the browser blocks it (VPN, extension, popup blocker,
      // sandboxed iframe), show a visible fallback link the user can click.
      const targetUrl = data.url;
      try {
        window.location.assign(targetUrl);
      } catch (err) {
        console.error("assign failed", err);
      }
      // Safety net: if 2s later we are still on /pago, expose a clickable fallback.
      setTimeout(() => {
        if (window.location.pathname.startsWith("/pago")) {
          setFallbackUrl(targetUrl);
          setLoading(false);
          toast.info("Si no se abre solo, pulsa el botón azul para ir al pago seguro.");
        }
      }, 2000);
    } catch (e) {
      console.error("Stripe checkout error:", e?.response?.status, e?.response?.data || e?.message);
      const detail = e?.response?.data?.detail;
      toast.error(
        detail ||
          (e?.code === "ECONNABORTED"
            ? "El servidor tardó demasiado. Intenta de nuevo."
            : "No se pudo crear la sesión de pago. Intenta de nuevo.")
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--mep-black)] text-white mep-grain">
      <header className="px-5 md:px-10 py-5">
        <button
          data-testid="payment-back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-[var(--mep-text-muted)] hover:text-white transition"
        >
          <ArrowLeft size={16} /> Volver al inicio
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mep-pill-gold mb-5">Acceso premium</div>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter mb-3">
            Selecciona tu país
          </h1>
          <p className="text-[var(--mep-text-muted)] mb-10 max-w-xl">
            Un solo pago. Sin mensualidades. Acceso completo de por vida a rutinas, comidas, snacks, suplementos y seguimiento.
          </p>

          <div className="space-y-4">
            {pricing.map((p) => {
              const info = COUNTRY_INFO[p.country];
              const isSelected = selected === p.country;
              return (
                <button
                  key={p.country}
                  data-testid={`pay-country-${p.country}`}
                  onClick={() => selectCountry(p.country)}
                  className={`w-full text-left p-6 rounded-xl border transition-all flex items-center justify-between gap-4 ${
                    isSelected
                      ? "bg-[var(--mep-surface-2)] border-[var(--mep-pink)]"
                      : "bg-[var(--mep-surface)] border-[var(--mep-border)] hover:border-[var(--mep-gold)]/50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{info.flag}</span>
                    <div>
                      <div className="font-display text-xl font-light tracking-tight">{info.title}</div>
                      <div className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mt-1">
                        Pago único
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl md:text-3xl text-[var(--mep-gold)]">{info.price}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col gap-3">
            <button
              ref={continueBtnRef}
              data-testid="pay-checkout-btn"
              onClick={handlePay}
              disabled={!selected || loading}
              className="btn-pink w-full py-5 text-base font-semibold flex items-center justify-center gap-2"
            >
              {loading ? "Redirigiendo a pago seguro..." : "Continuar al pago"}
            </button>

            {fallbackUrl && (
              <a
                data-testid="pay-checkout-fallback-link"
                href={fallbackUrl}
                target="_self"
                rel="noopener noreferrer"
                className="w-full py-4 rounded-full border border-[var(--mep-pink)] bg-[rgba(232,61,132,0.10)] text-[var(--mep-pink)] hover:bg-[rgba(232,61,132,0.18)] transition flex items-center justify-center gap-2 font-medium text-sm"
              >
                <ExternalLink size={14} /> Abrir Stripe Checkout manualmente
              </a>
            )}

            <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs text-[var(--mep-text-muted)] uppercase tracking-[0.2em]">
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> Pago seguro</span>
              <span className="flex items-center gap-1.5"><CreditCard size={14} /> Tarjeta</span>
              <span className="flex items-center gap-1.5"><Smartphone size={14} /> Apple/Google Pay</span>
            </div>

            {/* Cuba manual payment */}
            <div className="mt-6 pt-6 border-t border-[var(--mep-border)]">
              <p className="text-xs text-center text-[var(--mep-text-muted)] mb-3 uppercase tracking-[0.2em]">
                ¿Estás en Cuba?
              </p>
              <a
                data-testid="pay-cuba-whatsapp-btn"
                href={`https://wa.me/5358697465?text=${encodeURIComponent("Hola, quiero comprar acceso a Mi Entrenador Personal desde Cuba.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 rounded-full border border-[var(--mep-gold)]/50 text-[var(--mep-gold)] hover:bg-[rgba(212,175,55,0.08)] transition flex items-center justify-center gap-2 font-medium"
              >
                🇨🇺 Comprar desde Cuba
              </a>
              <p className="text-xs text-center text-[var(--mep-text-muted)] mt-3 leading-relaxed">
                Coordinaremos el pago por WhatsApp mediante EnZona, Transfermóvil o efectivo.
              </p>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
